import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type PlanType = "exam_week" | "monthly" | "annual" | null;

interface SubscriptionState {
  subscribed: boolean;
  plan: PlanType;
  subscriptionEnd: string | null;
  loading: boolean;
}

export const STRIPE_PLANS = {
  exam_week: {
    priceId: "price_1T4JhsBCV4my6bjLShZ3CCmw",
    productId: "prod_U2OUZPAU6lzjPR",
  },
  monthly: {
    priceId: "price_1T4JiHBCV4my6bjLjk6RTznM",
    productId: "prod_U2OV8tCJ8QcvJH",
  },
  annual: {
    priceId: "price_1T4JijBCV4my6bjLWDEfPNYQ",
    productId: "prod_U2OVewp7EOLQ4C",
  },
} as const;

export function useSubscription() {
  const { user, session } = useAuth();
  const [state, setState] = useState<SubscriptionState>({
    subscribed: false,
    plan: null,
    subscriptionEnd: null,
    loading: true,
  });

  const checkSubscription = useCallback(async () => {
    if (!user || !session) {
      setState({ subscribed: false, plan: null, subscriptionEnd: null, loading: false });
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke("check-subscription");
      if (error) throw error;

      setState({
        subscribed: data?.subscribed ?? false,
        plan: data?.plan ?? null,
        subscriptionEnd: data?.subscription_end ?? null,
        loading: false,
      });
    } catch {
      setState(prev => ({ ...prev, loading: false }));
    }
  }, [user, session]);

  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  // Auto-refresh when returning from checkout (?checkout=success)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") === "success") {
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        await checkSubscription();
        if (attempts >= 6) clearInterval(poll);
      }, 2000);
      const url = new URL(window.location.href);
      url.searchParams.delete("checkout");
      window.history.replaceState({}, "", url.pathname + url.search);
      return () => clearInterval(poll);
    }
    return undefined;
  }, [checkSubscription]);

  // Auto-refresh every 60s
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(checkSubscription, 60_000);
    return () => clearInterval(interval);
  }, [user, checkSubscription]);

  const checkout = async (plan: keyof typeof STRIPE_PLANS) => {
    const { data, error } = await supabase.functions.invoke("create-checkout", {
      body: { plan },
    });
    if (error) throw error;
    if (data?.url) {
      const win = window.open(data.url, "_blank");
      if (!win || win.closed || typeof win.closed === "undefined") {
        window.location.href = data.url;
      }
    }
  };

  const manageSubscription = async () => {
    const { data, error } = await supabase.functions.invoke("customer-portal");
    if (error) throw error;
    if (data?.url) {
      const win = window.open(data.url, "_blank");
      if (!win || win.closed || typeof win.closed === "undefined") {
        window.location.href = data.url;
      }
    }
  };

  return {
    ...state,
    checkout,
    manageSubscription,
    refresh: checkSubscription,
  };
}
